<template>
    <div class="visibility-changer">
        <div v-if="alertMessage">
            <a-alert
                :message="$t('whatsapp_business_api.userVisibility')"
                :description="$t('whatsapp_business_api.userVisibilityDesc')"
                type="info"
                show-icon
            />
        </div>
        <div class="title" v-if="showTitle">{{ $t("mail_integration_detail.visibility") }}</div>

        <!-- Kullanıcı Erişebilirliği Section -->
        <div class="section">
            <div class="section-title">Kullanıcı Erişebilirliği</div>
            <div class="section-description">
                Aşağıda belirtilen kişiler bu kanaldaki kişi ve chatları görebilir (sahiplik varsa erişemez). Bu ayar şuralarda aktif olarak rol alır:
                <ul>
                    <li><strong>Otomatik chat atama:</strong> Bu kanala gelen müşteriler buradaki gizliliğe göre atama yapılır, bu kanalara erişimi olmayanlara chat vermez</li>
                    <li><strong>Yönlendirme işlemleri:</strong> Başka bir yerden chat yönlendirme veya chat'e başka bir temsilci eklenebilmesi için buradaki ayarlarla örtüşmesi lazım, izin verilemeyen kişiye yönlendirme yapılmaz</li>
                    <li><strong>Müşteri/kişi listeleri:</strong> Gördüğünüz listeler bu ayara göre gelir, göremediğiniz kişiler varsa buradaki ayarla ilgili olabilir</li>
                    <li><strong>Sahiplendirme:</strong> Bir müşteriyi bir temsilciye sahiplendirmek isterseniz yine bu ayar sınırında yapabilirsiniz</li>
                </ul>
            </div>

            <a-alert type="info" show-icon v-if="getIsPublic()" :message="$t('message.visibilityInfoCont')" />

            <div v-else class="lines">
                <div class="subtitle">{{ $t("message.visibleAgents") }}</div>
                <div class="agents">
                    <div>
                        <button @click="openAgentModal" class="add-btn">
                            <span class="md-icon" style="color: #fff; padding-right: 2px"> add </span>
                        </button>
                    </div>
                    <MultipleAvatar :id-list="cloneUserIds" class="visibility-user-avatars" />
                </div>

                <div class="subtitle">{{ $t("message.visibleDepartments") }}</div>
                <div class="agents">
                    <div v-for="id of cloneDepartmentIds" class="agent" :key="id">
                        <Avatar
                            :size="50"
                            :src="undefined"
                            :username="getDepartmentTitle(getDepartment(id))"
                            class="mr-2 visibility-avatar"
                        />
                        <div
                            class="delete-icon"
                            @click="deleteDepartment(id)"
                            v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                            :content="getDepartmentTitle(getDepartment(id))"
                        ></div>
                    </div>
                    <div>
                        <button @click="openDepartmentModal" class="add-btn">
                            <span class="md-icon" style="color: #fff; padding-right: 2px"> add </span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="section-buttons">
                <a-button type="primary" @click="openAgentModal" class="visibility-button">
                    {{ $t("message.editAgentVisibility") }}</a-button
                >
                <a-button type="primary" @click="openDepartmentModal" class="visibility-button">
                    {{ $t("message.editDepartmentVisibility") }}</a-button
                >
                <a-button type="danger" @click="makePublic" class="visibility-button" v-if="!getIsPublic()">
                    Herkese Açık Yap
                </a-button>
            </div>
        </div>

        <a-divider />

        <!-- Kanal Sahipleri Section -->
        <div class="section">
            <div class="section-title">Kanal Sahipleri</div>
            <div class="section-description">
                Kanal sahipleri bu kanaldaki tüm konuşmaları görüntüleme yetkisine sahiptir.
                Görünürlük ayarlarından bağımsız olarak tüm mesajları okuyabilirler.
            </div>
            <div class="lines">
                <div class="agents">
                    <div v-for="id of getOwnerIds()" class="agent" :key="id">
                        <Avatar
                            :size="50"
                            :src="getUser(id) ? getUser(id).gravatar : undefined"
                            :username="getUser(id) ? getUser(id).name : 'N/A'"
                            class="mr-2 visibility-avatar"
                        />
                        <div
                            class="delete-icon"
                            @click="deleteOwner(id)"
                            v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                            :content="getUser(id) ? getUser(id).name : 'N/A'"
                        ></div>
                    </div>
                    <div>
                        <button @click="openOwnerModal" class="add-btn">
                            <span class="md-icon" style="color: #fff; padding-right: 2px"> add </span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="section-buttons">
                <a-button type="primary" @click="openOwnerModal" class="visibility-button">
                    Kanal Sahiplerini Düzenle
                </a-button>
            </div>
        </div>

        <!-- Owners Modal -->
        <FormModal
            title="Kanal Sahibi Seç"
            subtitle="Bu kanalın sahibi olacak temsilcileri seçin"
            v-if="visibleOwnerModal"
            v-model="cloneOwnerIds"
            @cancel="visibleOwnerModal = false"
        >
            <template #body>
                <div class="pf-main">
                    <div class="pr-search">
                        <input :placeholder="$t('message.searchRepresentative')" v-model="searchText" />
                        <span class="material-icons mx-2 pr-icon"> search </span>
                    </div>
                    <hr />
                    <div class="pr-items pr-item-wrapper">
                        <div class="pr-item" v-for="agent of getFilteredUsers()" :key="agent._id" @click="toggleOwner(agent._id)">
                            <a-checkbox class="segment-checkbox" :checked="cloneOwnerIds.includes(agent._id)" />

                            <div class="user-data-wrapper">
                                <div class="user-data">
                                    <Avatar
                                        :size="30"
                                        :src="agent.gravatar"
                                        :username="agent.name"
                                        class="mr-2 visibility-avatar"
                                    />
                                    <div class="metas">
                                        <div class="pr-item-name">{{ agent.name }}</div>
                                        <div>
                                            {{ getAgentDepartmentNames(agent) }}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="pr-item-status"
                                    :data-online="$_.get(user, '_id') === agent._id ? true : agent.isOnline"
                                    :data-status="agent.status.toLowerCase()"
                                >
                                    <div class="pr-item-status-circle"></div>
                                    {{ agent.status }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="actions">
                    <a-button class="pr-confirm-btn" @click="handleSave" type="primary">
                        {{ $t("message.save") }}
                    </a-button>
                </div>
            </template>
        </FormModal>

        <FormModal
            :title="$t('message.selectAgent')"
            :subtitle="$t('message.selectAgentDesc')"
            v-if="visibleAgentModal"
            v-model="cloneUserIds"
            @cancel="visibleAgentModal = false"
        >
            <template #body>
                <div class="pf-main">
                    <div class="pr-search">
                        <input :placeholder="$t('message.searchRepresentative')" v-model="searchText" />
                        <span class="material-icons mx-2 pr-icon"> search </span>
                    </div>
                    <hr />
                    <div class="pr-items pr-item-wrapper">
                        <div class="pr-item" v-for="agent of getFilteredUsers()" :key="agent._id" @click="toggleAgent(agent._id)">
                            <a-checkbox class="segment-checkbox" :checked="cloneUserIds.includes(agent._id)" />

                            <div class="user-data-wrapper">
                                <div class="user-data">
                                    <Avatar
                                        :size="30"
                                        :src="agent.gravatar"
                                        :username="agent.name"
                                        class="mr-2 visibility-avatar"
                                    />
                                    <div class="metas">
                                        <div class="pr-item-name">{{ agent.name }}</div>
                                        <div>
                                            {{ getAgentDepartmentNames(agent) }}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="pr-item-status"
                                    :data-online="$_.get(user, '_id') === agent._id ? true : agent.isOnline"
                                    :data-status="agent.status.toLowerCase()"
                                >
                                    <div class="pr-item-status-circle"></div>
                                    {{ agent.status }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="actions">
                    <a-button class="pr-confirm-btn" @click="handleSave" type="primary">
                        {{ $t("message.save") }}
                    </a-button>
                </div>
            </template>
        </FormModal>
        <FormModal
            :title="$t('message.selectDepartment')"
            :subtitle="$t('message.selectDepartmentDesc')"
            v-if="visibleDepartmentModal"
            v-model="cloneDepartmentIds"
            @cancel="visibleDepartmentModal = false"
        >
            <template #body>
                <div class="pf-main">
                    <div class="pr-search">
                        <input :placeholder="$t('message.searchRepresentative')" v-model="searchText" />
                        <span class="material-icons mx-2 pr-icon"> search </span>
                    </div>
                    <hr />
                    <div class="pr-items pr-item-wrapper">
                        <div
                            class="pr-item"
                            v-for="item of getFilteredDepartments()"
                            :key="item._id"
                            @click="toogleDepartment(item._id)"
                        >
                            <a-checkbox class="segment-checkbox" :checked="cloneDepartmentIds.includes(item._id)" />

                            <div class="user-data-wrapper">
                                <div class="user-data">
                                    <Avatar
                                        :size="30"
                                        :src="undefined"
                                        :username="item.languageContent[$i18n.locale].name"
                                        class="mr-2 visibility-avatar"
                                    />
                                    <div class="metas">
                                        <div class="pr-item-name">
                                            {{ item.languageContent[$i18n.locale].name }}
                                        </div>
                                    </div>
                                </div>

                                <div class="pr-item-status">
                                    <div class="avatar-group">
                                        <Avatar
                                            v-for="user of getDepartmentUsers(item._id, 2)"
                                            :key="user._id"
                                            :size="30"
                                            :src="user.gravatar"
                                            :username="user.name"
                                            class="avatar-wrapper"
                                        />
                                        <div
                                            class="extra avatar-wrapper"
                                            v-if="getDepartmentUsers(item._id).length > 2"
                                        >
                                            +{{ getDepartmentUsers(item._id, 2).length - 2 }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="actions">
                    <a-button class="pr-confirm-btn" @click="handleSave" type="primary">
                        {{ $t("message.save") }}
                    </a-button>
                </div>
            </template>
        </FormModal>
    </div>
</template>

<script>
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import FormModal from "../../../components/FormModal.vue";
import _ from "lodash";
import Avatar from "vue-avatar";
import MultipleAvatar from "../../../components/common/MultipleAvatar.vue";

export default Vue.extend({
    name: "VisibilityChanger",
    components: { FormModal, Avatar, MultipleAvatar },
    props: {
        value: {
            type: Object,
            default: () => ({
                users: [],
                departments: [],
                owners: [],
            }),
        },
        showTitle: {
            type: Boolean,
            default: true,
        },
        alertMessage: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            visibleAgentModal: false,
            visibleDepartmentModal: false,
            visibleOwnerModal: false,
            cloneUserIds: [],
            cloneDepartmentIds: [],
            cloneOwnerIds: [],
            searchText: "",
            isInternalUpdate: false,
        };
    },
    computed: {
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        ...mapState({
            users: (state) => state.user.users.data,
            user: (state) => state.auth.user,
            departments: (state) => state.subject.subjects.data,
            site: (state) => state.site.site,
        }),
        displayUserIds() {
            return this.cloneUserIds;
        },
        displayDepartmentIds() {
            return this.cloneDepartmentIds;
        },
        displayOwnerIds() {
            return this.cloneOwnerIds;
        },
    },
    methods: {
        openAgentModal() {
            this.searchText = "";
            this.visibleAgentModal = true;
        },
        openDepartmentModal() {
            this.searchText = "";
            this.visibleDepartmentModal = true;
        },
        openOwnerModal() {
            this.searchText = "";
            this.visibleOwnerModal = true;
        },
        getDepartmentTitle(department) {
            if (department === undefined) return "N/A";
            const langs = Object.keys(department.languageContent);
            const targetLang = langs.includes(this.$i18n.locale) ? this.$i18n.locale : langs[0];
            return _.get(department, `languageContent.${targetLang}.name`);
        },
        getUser(userId) {
            return this.users.find((user) => user._id === userId);
        },
        getIsPublic() {
            return this.cloneUserIds.length === 0 && this.cloneDepartmentIds.length === 0;
        },
        getDepartment(id) {
            return this.departments.find((department) => department._id === id);
        },
        getFilteredUsers() {
            let q = this.searchText.split("").join(".*");
            return this.users.filter((x) => new RegExp(q, "gi").test(x.name));
        },
        getFilteredDepartments() {
            let q = this.searchText.split("").join(".*");
            return this.departments.filter((x) => {
                const departmentName = this.getDepartmentTitle(x);
                return new RegExp(q, "gi").test(departmentName);
            });
        },
        deleteAgent(agentId) {
            this.toggleAgent(agentId);
            this.handleSaveImmediate();
        },
        deleteDepartment(departmentId) {
            this.toogleDepartment(departmentId);
            this.handleSaveImmediate();
        },
        deleteOwner(ownerId) {
            this.toggleOwner(ownerId);
            this.handleSaveImmediate();
        },
        toggleAgent(agentId) {
            this.cloneUserIds = this.cloneUserIds.includes(agentId)
                ? this.cloneUserIds.filter((x) => x !== agentId)
                : [...this.cloneUserIds, agentId];
        },
        toogleDepartment(departmentId) {
            this.cloneDepartmentIds = this.cloneDepartmentIds.includes(departmentId)
                ? this.cloneDepartmentIds.filter((x) => x !== departmentId)
                : [...this.cloneDepartmentIds, departmentId];
        },
        toggleOwner(ownerId) {
            this.cloneOwnerIds = this.cloneOwnerIds.includes(ownerId)
                ? this.cloneOwnerIds.filter((x) => x !== ownerId)
                : [...this.cloneOwnerIds, ownerId];
        },
        handleSaveImmediate() {
            this.isInternalUpdate = true;

            this.$emit("input", {
                users: [...this.cloneUserIds],
                departments: [...this.cloneDepartmentIds],
                owners: [...this.cloneOwnerIds],
            });

            setTimeout(() => {
                this.isInternalUpdate = false;
            }, 100);
        },
        handleSave() {
            this.isInternalUpdate = true;

            this.visibleAgentModal = false;
            this.visibleDepartmentModal = false;
            this.visibleOwnerModal = false;

            this.$nextTick(() => {
                this.$emit("input", {
                    users: [...this.cloneUserIds],
                    departments: [...this.cloneDepartmentIds],
                    owners: [...this.cloneOwnerIds],
                });

                setTimeout(() => {
                    this.isInternalUpdate = false;
                }, 100);
            });
        },
        getAgentDepartmentNames(user) {
            return (user.subjects || []).length
                ? this.departments
                    .filter((x) => (user.subjects || []).includes(x._id))
                    .map(
                        (x) =>
                            _.get(x, `languageContent.${this.$i18n.locale}.name`) ||
                            _.get(x, `languageContent.${Object.keys(x.languageContent)[0]}.name`)
                    )
                    .join(",")
                    .toLowerCase()
                : this.$t("message.allDepartments");
        },
        getDepartmentUsers(departmentId, take) {
            const users = this.users.filter((x) => (x.subjects || []).includes(departmentId));
            if (take) {
                return users.slice(0, take);
            } else {
                return users;
            }
        },
        getOwnerIds() {
            return this.cloneOwnerIds || [];
        },
        makePublic() {
            const self = this;
            this.$confirm({
                title: "Bu kanalı herkese açık yapmak istediğinize emin misiniz?",
                content: "Bu işlem sonrasında tüm temsilciler bu kanaldaki konuşmaları görebilecektir.",
                okText: "Evet, Herkese Açık Yap",
                cancelText: "İptal",
                onOk() {
                    self.cloneUserIds = [];
                    self.cloneDepartmentIds = [];
                    self.handleSaveImmediate();
                    self.$message.success("Kanal herkese açık hale getirildi");
                },
            });
        },
    },
    mounted() {
        if (this.value) {
            this.cloneUserIds = [...(this.value.users || [])];
            this.cloneDepartmentIds = [...(this.value.departments || [])];
            this.cloneOwnerIds = [...(this.value.owners || [])];
        }
    },
    watch: {
        value: {
            handler: function (val, oldVal) {
                if (!val) return;

                const usersChanged = !_.isEqual(val.users, oldVal ? oldVal.users : []);
                const departmentsChanged = !_.isEqual(val.departments, oldVal ? oldVal.departments : []);
                const ownersChanged = !_.isEqual(val.owners, oldVal ? oldVal.owners : []);

                if (usersChanged || departmentsChanged || ownersChanged) {
                    const currentUsersMatch = _.isEqual(val.users, this.cloneUserIds);
                    const currentDepartmentsMatch = _.isEqual(val.departments, this.cloneDepartmentIds);
                    const currentOwnersMatch = _.isEqual(val.owners, this.cloneOwnerIds);

                    if (this.isInternalUpdate && currentUsersMatch && currentDepartmentsMatch && currentOwnersMatch) {
                        return;
                    }

                    if (usersChanged && !currentUsersMatch) {
                        this.cloneUserIds = [...(val.users || [])];
                    }
                    if (departmentsChanged && !currentDepartmentsMatch) {
                        this.cloneDepartmentIds = [...(val.departments || [])];
                    }
                    if (ownersChanged && !currentOwnersMatch) {
                        this.cloneOwnerIds = [...(val.owners || [])];
                    }
                }
            },
            deep: true,
            immediate: false,
        },
    },
});
</script>

<style scoped lang="scss">
.visibility-changer {
    display: flex;
    flex-direction: column;
    gap: 15px;

    .section {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .section-buttons {
            display: flex;
            gap: 5px;
            margin-top: 10px;

            .visibility-button {
                word-wrap: break-word;
                white-space: normal;
                height: max-content;
            }
        }
    }

    .lines {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .title {
        font-weight: 700;
        color: var(--color-text-strong-default);
        font-size: 18px;
        line-height: 32px;
    }

    .section-title {
        font-weight: 700;
        color: var(--color-text-strong-default);
        font-size: 16px;
        line-height: 28px;
    }

    .subtitle {
        font-weight: 700;
        color: var(--color-text-strong-default);
        font-size: 14px;
        line-height: 32px;
    }

    .section-description {
        font-size: 13px;
        color: var(--color-text-default);
        line-height: 1.4;

        ul {
            margin: 10px 0;
            padding-left: 20px;

            li {
                margin-bottom: 5px;

                strong {
                    color: var(--color-text-strong-default);
                }
            }
        }
    }

    .agents {
        display: flex;
        flex-direction: row;
        gap: 5px;
        flex-wrap: wrap;

        .agent {
            position: relative;

            &:hover {
                .delete-icon {
                    display: grid;
                }
            }
        }
    }
}

.pf-main {
    display: flex;
    flex-direction: column;
    background: var(--color-bg-default);
    border: 1px solid var(--color-border-default);
    padding: 5px;

    .pr-search {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        width: 100%;

        input {
            border: 1px solid #c6cdd4 !important;
            width: 100%;
            padding: 10px;
            padding-right: 40px;
            border-radius: 5px;
            transition: all 0.3s;
        }

        input:focus {
            border: 1px solid black !important;
        }

        .pr-icon {
            position: absolute;
            right: 4px;
        }
    }

    .pr-items {
        min-height: 300px;
        max-height: 300px;
        overflow: auto;

        .pr-item:last-child {
            border-bottom: none;
        }

        .pr-item {
            display: flex;
            justify-content: start;
            align-items: center;
            flex-wrap: nowrap;
            padding: 5px 10px;
            border-radius: 4px;
            cursor: pointer;
            gap: 10px;
            border-bottom: 1px solid var(--color-border-default);

            &:hover {
                background: var(--color-bg-neutral-hover);
            }

            .user-data-wrapper {
                width: 100%;
                display: flex;
                justify-content: space-between;

                padding: 0 5px;

                .user-data {
                    display: flex;
                    gap: 5px;
                    align-items: center;

                    .visibility-avatar {
                        color: var(--color-text-onprimary-default);
                    }

                    .metas {
                        display: flex;
                        flex-direction: column;
                    }
                }
            }

            .pr-item-status {
                display: flex;
                justify-content: center;
                align-items: center;

                .pr-item-status-circle {
                    width: 10px;
                    height: 10px;
                    border-radius: 100%;
                    margin-right: 2px;
                }

                &[data-online="true"] {
                    &[data-status="online"] {
                        color: #1bc5bd !important;

                        .pr-item-status-circle {
                            background: #1bc5bd !important;
                        }
                    }

                    &[data-status="away"] {
                        color: #3699ff !important;

                        .pr-item-status-circle {
                            background: #3699ff !important;
                        }
                    }

                    &[data-status="busy"] {
                        color: #ffa829 !important;

                        .pr-item-status-circle {
                            background: #ffa829 !important;
                        }
                    }

                    &[data-status="offline"] {
                        color: #f64e60 !important;

                        .pr-item-status-circle {
                            background: #f64e60 !important;
                        }
                    }
                }

                &:not([data-online]) {
                    &[data-status="online"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }

                    &[data-status="away"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }

                    &[data-status="busy"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }

                    &[data-status="offline"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }
                }
            }
        }
    }
}

.add-btn {
    height: 50px;
    width: 50px;
    border-radius: 50%;
    border: 1px dashed #696a6b;
    background: #ffffff;
    .md-icon {
        color: #696a6b !important;
    }
}

.add-btn:hover {
    background: #696a6b;
}

.avatar-group {
    display: inline-flex;
}

.avatar-wrapper {
    margin-inline-start: -8px;

    &.extra {
        width: 34px;
        height: 32px;
        border-radius: 50%;
        border-color: white;
        color: rgb(245, 106, 0);
        background-color: rgb(253, 227, 207);
        display: flex;
        justify-content: center;
        align-items: center;
    }
}

.avatar-wrapper:first-child {
    margin-inline-start: unset;
}

.delete-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.4);
    position: absolute;
    top: 0;
    left: 0;
    display: none;
    place-items: center;
    cursor: pointer;

    &:after {
        display: inline-block;
        content: "\00d7";
        color: rgba(255, 255, 255, 0.8);
        font-size: 35px;
    }
}
</style>
<style lang="scss">
.visibility-avatar {
    span {
        color: var(--color-text-onprimary-default);
    }
}
.visibility-user-avatars {
    margin-left: -15px;
}
</style>
